import * as Sentry from '@sentry/browser';
import { format } from 'date-fns';
import posthog from 'posthog-js';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { DatePicker } from '@/components/common/DatePicker';
import SubmitButton from '@/components/common/SubmitButton';
import type { ModalProps } from '@/components/providers/ModalProvider/ModalProvider';
import { Field, FieldLabel } from '@/components/shadcn/field';
import { Input } from '@/components/shadcn/input';
import { Textarea } from '@/components/shadcn/textarea';
import useAnvaEmployees from '@/features/crm/queries/hooks/useAnvaEmployees';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import useCreateNote from '@/features/notes/mutations/useCreateNote';
import type { Party } from '@/features/party/types';
import useAuth from '@/hooks/useAuth';

import useActionsAnva from '../../hooks/useActionsAnva';
import useOnlyRelevantAnvaPolicyRef from '../../hooks/useOnlyRelevantAnvaPolicyRef';
import type { CreateTaskVariablesAnva } from '../../types';
import AssigneeField from '../AssigneeField';
import PriorityField from '../PriorityField';

import AnvaReasonField from './AnvaReasonField';
import AnvaTargetField from './AnvaTargetField';

type CreateTaskValues = {
  title: string;
  description: string;
  priority: 0 | 1 | 2 | 3;
  date: Date;
  target:
    | {
        type: 'party';
        id: string;
      }
    | {
        type: 'policy';
        id: string;
      };
  assignee?: { label: string; value: string };
  reason?: string;
};

type Props = ModalProps & {
  defaultValues?: Partial<CreateTaskVariablesAnva>;
  party: Maybe<Party>;
};

const CRM_NAME = 'ANVA';
const CRM_KEY = 'anva';

export default function CreateTaskModalAnva({
  onClose,
  defaultValues,
  party,
}: Props) {
  const { t } = useTranslation();

  const { integrations } = useAuth();
  const { data: employees } = useAnvaEmployees();

  const brokerAnvaEmployee = employees?.find(
    (employee) => employee.value === integrations?.anva_preferred_agent_nr,
  );

  const onlyRelevantPolicyRef = useOnlyRelevantAnvaPolicyRef(party);
  const defaultPolicyId = defaultValues?.crmPolicyId ?? onlyRelevantPolicyRef;

  const { getSelectedMessageId, getSelectedMessageSender } = useEmailClient();
  const sender = getSelectedMessageSender();

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<CreateTaskValues>({
    defaultValues: {
      title: defaultValues?.title,
      description: defaultValues?.description,
      date: defaultValues?.dueDate
        ? new Date(defaultValues.dueDate)
        : new Date(),
      target: defaultPolicyId
        ? { type: 'policy', id: defaultPolicyId }
        : {
            type: 'party',
            id: party?.id,
          },
      assignee: brokerAnvaEmployee,
      priority: defaultValues?.priority ?? 0,
    },
  });

  const crmPartyId = party?.external_refs.find(
    (ref) => ref.company.toLowerCase() === CRM_KEY,
  )?.ref;

  const { createTask } = useActionsAnva();
  const { mutateAsync: createNote } = useCreateNote();

  const onSubmit: SubmitHandler<CreateTaskValues> = async (data) => {
    if (!crmPartyId) return;

    try {
      await createTask?.(
        {
          title: data.title,
          description: data.description,
          priority: data.priority,
          dueDate: format(data.date, 'yyyy-MM-dd'),
          assignedTo: data.assignee?.value,
          reason: data.reason,
          contactPersonName: sender?.name,
          contactPersonEmail: sender?.address,
          crmPolicyId:
            data.target.type === 'policy' ? data.target.id : undefined,
          crmPartyId,
        },
        {
          onSuccess: async () => {
            posthog.capture('Task created', {
              crmBackend: CRM_KEY,
              partyId: party.id,
              messageId: getSelectedMessageId(),
              priority: data.priority,
              hasAssignee: Boolean(data.assignee?.value),
              targetType: data.target.type,
              reason: data.reason,
            });
            toast.success(t('TASK_CREATED', { crm: CRM_NAME }));

            const noteHtml = `<p><strong>${t('TASK_CREATED_NOTE_PREFIX', {
              crm: CRM_NAME,
            })}:</strong> ${data.title}</p><p>${data.description}</p>`;

            await createNote({
              partyId: party.id,
              title: data.title,
              text: noteHtml,
            });
            onClose();
          },
          onError: (error) => {
            toast.error(t('TASK_CREATE_FAILED'));
            Sentry.captureException(error);
          },
        },
      );

      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 p-2">
      <div className="flex flex-col gap-2">
        <Field>
          <FieldLabel htmlFor="title">{t('TASK_FIELD_TITLE')}</FieldLabel>
          <Input
            {...register('title', { required: true })}
            placeholder={t('TASK_FIELD_TITLE_PH')}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="description">
            {t('TASK_FIELD_DESCRIPTION')}
          </FieldLabel>
          <Textarea
            {...register('description', { required: true })}
            placeholder={t('TASK_FIELD_DESCRIPTION_PH')}
            className="h-48"
          />
        </Field>

        <PriorityField<CreateTaskValues>
          control={control}
          priorities={[
            { label: t('PRIORITY_ANVA_0'), value: 0 },
            { label: t('PRIORITY_ANVA_1'), value: 1 },
            { label: t('PRIORITY_ANVA_2'), value: 2 },
            { label: t('PRIORITY_ANVA_3'), value: 3 },
          ]}
        />

        <Controller
          control={control}
          name="date"
          render={({ field }) => (
            <Field>
              <FieldLabel>{t('TASK_FIELD_DUE_DATE')}</FieldLabel>
              <DatePicker {...field} />
            </Field>
          )}
        />

        <AnvaTargetField<CreateTaskValues> control={control} party={party} />

        <AssigneeField control={control} employees={employees} />

        <AnvaReasonField control={control} />
      </div>

      <div className="flex justify-end">
        <SubmitButton isSubmitting={isSubmitting}>{t('CREATE')}</SubmitButton>
      </div>
    </form>
  );
}
